import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';
import type { Toast } from 'react-hot-toast/headless';
import toast, { useToaster } from 'react-hot-toast/headless';
import styled from 'styled-components';

import { Message } from '../../molecules/message';
import { SmallMessage } from '../../molecules/small-message';

export type MessageType = 'info' | 'warning' | 'danger';
export type MessageVariant = 'small' | 'normal';

/** We're using toast position to differentiate between normal and small tosts */
const MESSAGE_POSITION = 'top-center';
const SMALL_MESSAGE_POSITION = 'bottom-center';

const ToastsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface ToastElementProps {
  toastElement: Toast;
  textMessage: ReactNode;
  type: MessageType;
  isCloseButtonShown: boolean;
  variant?: MessageVariant;
}

const ToastElement = ({
  toastElement,
  textMessage,
  type,
  isCloseButtonShown,
  variant = 'normal',
}: ToastElementProps) => {
  const handleCloseButtonClick = () => {
    toast.dismiss(toastElement.id);
  };

  return variant === 'small' ? (
    <SmallMessage isCloseButtonShown={isCloseButtonShown} type={type} onCloseButtonClick={handleCloseButtonClick}>
      {textMessage}
    </SmallMessage>
  ) : (
    <Message isCloseButtonShown={isCloseButtonShown} type={type} onCloseButtonClick={handleCloseButtonClick}>
      {textMessage}
    </Message>
  );
};

interface ToasterProps {
  className?: string;
  variant?: MessageVariant;
}

export const Toaster = ({ className, variant = 'normal' }: ToasterProps) => {
  const { toasts, handlers } = useToaster();
  const { startPause, endPause } = handlers;

  const toastsToShow = useMemo(
    () =>
      toasts
        .filter((toastElement) => toastElement.visible)
        .filter(
          (toastElement) => toastElement.position === (variant === 'small' ? SMALL_MESSAGE_POSITION : MESSAGE_POSITION)
        ),
    [toasts, variant]
  );

  return (
    <ToastsContainer onMouseEnter={startPause} onMouseLeave={endPause} className={className}>
      {toastsToShow.map((toastElement) => (
        <Fragment key={toastElement.id}>
          {typeof toastElement.message === 'function' ? toastElement.message(toastElement) : null}
        </Fragment>
      ))}
    </ToastsContainer>
  );
};

interface ShowToastmessageProps {
  textMessage: ReactNode;
  type?: MessageType;
  isCloseButtonShown?: boolean;
  duration?: number;
  variant?: MessageVariant;
}

const DEFAULT_DURATION = 10000;

export const showToastMessage = ({
  textMessage,
  type = 'info',
  isCloseButtonShown = true,
  duration = DEFAULT_DURATION,
  variant,
}: ShowToastmessageProps) => {
  toast(
    (toastElement) => (
      <ToastElement
        toastElement={toastElement}
        textMessage={textMessage}
        type={type}
        isCloseButtonShown={isCloseButtonShown}
        variant={variant}
      />
    ),
    { duration, position: variant === 'small' ? SMALL_MESSAGE_POSITION : MESSAGE_POSITION }
  );
};

export const SmallMessagesToaster = styled(Toaster)`
  position: fixed;
  top: ${({ theme }) => theme.spacing(28)};
  right: ${({ theme }) => theme.spacing(4)};
  width: inherit;
  z-index: 9999;
`;
